(ns io.daskul.webapp.hooks.list
  "React hooks for list views with filtering and search."
  (:require
   [uix.core :as uix]
   [re-frame.core :as rf]
   [io.daskul.api-contract.filters :as contract.filters]
   [io.daskul.webapp.aggregates.events :as agg.events]
   [io.daskul.webapp.aggregates.subs :as agg.subs]
   [io.daskul.webapp.hooks.list-params :as list-params]))

;; ============================================================================
;; use-list (simple — just fetches and returns list data)
;; ============================================================================

(defn use-list
  "Simple hook for loading and accessing a list of aggregates.

   Params:
   - agg-key: Aggregate keyword (e.g., :users, :persons)
   - query-params: Optional query params map for filtering/pagination

   Returns {:items vector
            :loading? boolean
            :error error-map}"
  ([agg-key] (use-list agg-key nil))
  ([agg-key query-params]
   (let [items @(rf/subscribe [::agg.subs/all agg-key])
         loading? @(rf/subscribe [::agg.subs/loading? agg-key])
         error @(rf/subscribe [::agg.subs/error agg-key])]

     (uix/use-effect
      (fn []
        (rf/dispatch [::agg.events/list agg-key query-params])
        js/undefined)
      [agg-key query-params])

     {:items items
      :loading? loading?
      :error error})))

;; ============================================================================
;; use-list-params (unified search + filters)
;; ============================================================================

(defn- counts-fields
  "Extract fields that need counts — segmented filters whose field is in the
   backend's counts group-by config."
  [filter-config group-by-fields]
  (let [allowed (set group-by-fields)]
    (->> filter-config
         (filter #(= :segmented (:widget %)))
         (filter #(contains? allowed (:field %)))
         (mapv :field))))

(defn- dispatch-counts!
  "Dispatch counts requests for each segmented filter field.
   Params are the same filters sent to list, minus the group-by field itself
   (to get counts across all values of that field)."
  [aggregate fields params]
  (doseq [field fields]
    (let [count-params (list-params/build-dispatch-params (dissoc params field))]
      (rf/dispatch [::agg.events/counts aggregate field count-params]))))

(defn use-list-params
  "Unified hook for search + filters + sort. Every dispatch sends combined params.
   Syncs state to/from URL query params when push-url-fn/replace-url-fn are provided.

   Options:
     :aggregate            - Aggregate keyword
     :filter-config        - Vector of filter configs from :list :filters
     :sort-config          - Map with :sort-fields and :default-sort from :list config
     :counts-group-by      - Vector of fields from backend counts group-by config (default [])
     :initial-query-params - URL query params from route match (may have string keys)
     :push-url-fn          - (fn [params]) pushState (deliberate actions, creates history)
     :replace-url-fn       - (fn [params]) replaceState (search typing, no history)
     :debounce-ms          - Search debounce (default 500)
     :min-length           - Min search length to trigger search (default 2)

   Returns:
     :query         - Current search string (for input binding)
     :set-query!    - (string) → update search (debounced dispatch)
     :clear-query!  - Clear search
     :filters       - Current filter values map {field param-string}
     :set-filter!   - (field value) → update filter + dispatch
     :set-range-filter! - (field :from|:to js/Date) → update range bound + dispatch
     :clear-filters! - Clear all filters + dispatch
     :has-active-filters? - Whether any non-default filters or search are active
     :sort          - Current sort vector (e.g., [[:name :asc]])
     :set-sort!     - (field) → toggle sort direction or set new field"
  [{:keys [aggregate filter-config sort-config counts-group-by active-context
           initial-query-params push-url-fn replace-url-fn debounce-ms min-length]
    :or {debounce-ms 500
         min-length 2
         counts-group-by []}}]
  (let [defaults (uix/use-memo (fn [] (list-params/default-filter-values filter-config active-context))
                               [filter-config active-context])
        ;; Active-context-flagged fields (e.g. branch-id) never round-trip
        ;; through the URL — they're derived from app state (SMS-238).
        ac-fields (uix/use-memo (fn [] (list-params/active-context-fields filter-config))
                                [filter-config])
        default-sort (:default-sort sort-config)

        ;; Parse URL params for initial state (URL wins over defaults)
        ;; :q is merged into :filters because params state includes :q for dispatch
        url-parsed (uix/use-memo
                    (fn []
                      (let [parsed (list-params/parse-url-params initial-query-params defaults ac-fields filter-config)
                            q (:q parsed)]
                        (cond-> parsed
                          (seq q) (update :filters assoc :q q))))
                    [initial-query-params defaults ac-fields filter-config])

        [params set-params!] (uix/use-state (:filters url-parsed))
        [query set-query-internal!] (uix/use-state (or (:q url-parsed) ""))
        [current-sort set-current-sort!] (uix/use-state (or (:sort url-parsed) default-sort))
        [page set-page-internal!] (uix/use-state (or (:page url-parsed) 1))
        [timeout-id set-timeout-id!] (uix/use-state nil)

        ;; Ref to track what WE last pushed to URL (to detect our own pushes vs back/forward)
        synced-qp-ref (uix/use-ref nil)
        ;; Ref to track the previous value of initial-query-params.
        ;; Guards against false re-dispatches when the effect fires due to
        ;; unstable deps (e.g., sync-url-replace! recreated each render).
        prev-iqp-ref (uix/use-ref nil)

        ;; Refs for mount-only dispatch values — read on mount to avoid
        ;; referencing state vars in the effect body (UIx exhaustive deps).
        init-params-ref (uix/use-ref (:filters url-parsed))
        init-query-ref (uix/use-ref (or (:q url-parsed) ""))
        init-sort-ref (uix/use-ref (or (:sort url-parsed) default-sort))
        init-page-ref (uix/use-ref (or (:page url-parsed) 1))

        ;; Fields that need counts (segmented filters in backend's group-by config)
        c-fields (uix/use-memo (fn [] (counts-fields filter-config counts-group-by))
                               [filter-config counts-group-by])

        ;; Dispatch with full params (includes sort + page)
        dispatch! (uix/use-callback
                   (fn [p sort-vec pg]
                     (let [sort-param (list-params/sort->param sort-vec)
                           full-params (cond-> (list-params/build-dispatch-params p)
                                         sort-param (assoc :sort sort-param)
                                         (and pg (> pg 1)) (assoc :page pg))]
                       (rf/dispatch [::agg.events/list aggregate full-params])
                       (when (seq c-fields)
                         (dispatch-counts! aggregate c-fields p))))
                   [aggregate c-fields])

        ;; URL sync helper — push for deliberate actions
        ;; Takes query explicitly to avoid stale closure (e.g., clear-filters! sets query to "")
        sync-url-push! (fn [filt q sort-vec pg]
                         (when push-url-fn
                           (let [url-params (list-params/build-url-params filt q sort-vec pg ac-fields default-sort)]
                             (push-url-fn url-params)
                             (reset! synced-qp-ref url-params))))

        ;; URL sync helper — replace for debounced search
        ;; Also used on mount to sync defaults to URL (must be stable for use-effect deps)
        sync-url-replace! (uix/use-callback
                           (fn [filt q sort-vec pg]
                             (when replace-url-fn
                               (let [url-params (list-params/build-url-params filt q sort-vec pg ac-fields default-sort)]
                                 (replace-url-fn url-params)
                                 (reset! synced-qp-ref url-params))))
                           [replace-url-fn ac-fields default-sort])]

    ;; Unified mount + back/forward effect.
    ;; On mount: dispatch with initial state (from URL or defaults).
    ;; On back/forward: detect when initial-query-params actually changed.
    ;;
    ;; Two guards prevent false re-dispatches:
    ;; 1. prev-iqp-ref: only act when initial-query-params truly changed value
    ;;    (the effect may fire due to unstable deps like sync-url-replace!)
    ;; 2. synced-qp-ref: skip when the change was caused by our own URL push
    ;;    (set-filter!/set-query! already dispatched directly)
    (uix/use-effect
     (fn []
       (let [curr-kw (list-params/keywordize-map initial-query-params)]
         (if (nil? @prev-iqp-ref)
           ;; Mount: dispatch with initialized state values + sync defaults to URL
           (do (reset! prev-iqp-ref curr-kw)
               (dispatch! @init-params-ref @init-sort-ref @init-page-ref)
               (sync-url-replace! @init-params-ref @init-query-ref @init-sort-ref @init-page-ref))
           ;; Subsequent: only act if initial-query-params actually changed
           (when (not= @prev-iqp-ref curr-kw)
             (reset! prev-iqp-ref curr-kw)
             ;; Skip if this URL change was our own push (we already dispatched)
             (when (not= (list-params/keywordize-map @synced-qp-ref) curr-kw)
               (let [parsed (list-params/parse-url-params initial-query-params defaults ac-fields filter-config)
                     q (:q parsed)
                     new-filters (cond-> (:filters parsed)
                                   (seq q) (assoc :q q))]
                 (set-params! new-filters)
                 (set-query-internal! (or q ""))
                 (set-current-sort! (or (:sort parsed) default-sort))
                 (set-page-internal! (or (:page parsed) 1))
                 (dispatch! new-filters
                            (or (:sort parsed) default-sort)
                            (or (:page parsed) 1)))))))
       js/undefined)
     [dispatch! defaults ac-fields filter-config default-sort initial-query-params sync-url-replace!])

    ;; Cleanup timeout on unmount
    (uix/use-effect
     (fn []
       (fn []
         (when timeout-id
           (js/clearTimeout timeout-id))))
     [timeout-id])

    {:query query
     :filters params
     :sort current-sort
     :page page

     :set-query!
     (fn [q]
       (set-query-internal! q)
       (set-page-internal! 1)
       (when timeout-id
         (js/clearTimeout timeout-id))
       (let [q-param (when (>= (count q) min-length) q)
             new-params (if q-param
                          (assoc params :q q-param)
                          (dissoc params :q))]
         (set-params! new-params)
         (set-timeout-id!
          (js/setTimeout
           (fn []
             (dispatch! new-params current-sort 1)
             (sync-url-replace! new-params q-param current-sort 1))
           debounce-ms))))

     :clear-query!
     (fn []
       (set-query-internal! "")
       (set-page-internal! 1)
       (when timeout-id
         (js/clearTimeout timeout-id))
       (let [new-params (dissoc params :q)]
         (set-params! new-params)
         (dispatch! new-params current-sort 1)
         (sync-url-push! new-params nil current-sort 1)))

     :set-filter!
     (fn [field value]
       (let [param (list-params/filter-value->param value)
             new-params (if param
                          (assoc params field param)
                          (dissoc params field))
             with-search (if (>= (count query) min-length)
                           (assoc new-params :q query)
                           new-params)]
         (set-params! with-search)
         (set-page-internal! 1)
         (dispatch! with-search current-sort 1)
         (sync-url-push! with-search query current-sort 1)))

     :set-range-filter!
     (fn [field bound date]
       ;; Param key comes from the shared wire contract, not a local suffix —
       ;; the server builds its accepted-param whitelist from the same fn (#653).
       (let [param-key (contract.filters/range-param field bound)
             iso (list-params/date->iso-string date)
             new-params (if iso
                          (assoc params param-key iso)
                          (dissoc params param-key))
             with-search (if (>= (count query) min-length)
                           (assoc new-params :q query)
                           new-params)]
         (set-params! with-search)
         (set-page-internal! 1)
         (dispatch! with-search current-sort 1)
         (sync-url-push! with-search query current-sort 1)))

     :set-sort!
     (fn [field]
       (let [current-field (ffirst current-sort)
             current-dir (second (first current-sort))
             new-sort (if (= field current-field)
                        [[field (if (= :asc current-dir) :desc :asc)]]
                        [[field :asc]])]
         (set-current-sort! new-sort)
         (set-page-internal! 1)
         (dispatch! params new-sort 1)
         (sync-url-push! params query new-sort 1)))

     :set-page!
     (fn [pg]
       (set-page-internal! pg)
       (dispatch! params current-sort pg)
       (sync-url-push! params query current-sort pg))

     :clear-filters!
     (fn []
       (set-query-internal! "")
       (set-page-internal! 1)
       (when timeout-id
         (js/clearTimeout timeout-id))
       (set-params! defaults)
       (set-current-sort! default-sort)
       (dispatch! defaults default-sort 1)
       (sync-url-push! defaults nil default-sort 1))

     :has-active-filters?
     (or (seq query)
         (not= params defaults))}))
